import {
    forwardRef,
    type AnchorHTMLAttributes,
    type ButtonHTMLAttributes,
    type ReactNode,
} from "react";

// Button — DS §4.1. Canonical tokens only.
//   primary  → --cta (coral) fill, white label (3.16:1, see its note), hover --cta-hover
//   outline  → transparent, 1px --border, --text-primary, lifts to --surface on hover
//   inverse  → --surface fill, --cta-ink text. Built for a DARK ground; every
//              other variant assumes --canvas or --surface behind it.
//   inverse-bold
//            → an alias of `primary` since the coral flip. See its note.
//   ghost    → no fill or stroke, keeps the size padding (real clickable box),
//              --ink-soft → --ink on a faint --bg-rail hover. Pairs with primary.
//   link     → no chrome and no padding, --success text, underline on hover
//   danger   → --danger fill, white
//   danger-outline
//            → --surface fill, 1px --danger-bd, --danger text, warming to
//              --danger-bg on hover. The RESTING state `danger-ghost` only
//              reaches on hover. For a destructive action that is the ONLY
//              action in its box and must read as destructive before the
//              cursor arrives — the Danger Zone's Archive. Outlined, not
//              filled: `danger` is a red slab that pulls the eye to the bottom
//              of a settings page it should not own.
//   danger-ghost
//            → reads as `ghost` at rest, resolves into a --danger-bd outlined
//              --surface pill with --danger text on hover. For a DESTRUCTIVE
//              action that sits inline beside ordinary ones — a Cancel next to
//              a Reschedule — where a filled `danger` would shout across a
//              whole list and a plain `ghost` never says what it does.
// Radius --radius-sm (6px) on all. focus-visible sage ring.

export type ButtonVariant =
    | "primary"
    | "outline"
    | "inverse"
    | "inverse-bold"
    | "ghost"
    | "link"
    | "danger"
    | "danger-outline"
    | "danger-ghost";

/**
 * `ghost`, but resolving into a WHITE hairline-outlined pill on hover instead of a rail-grey fill.
 *
 * The operator's ruling, first for the pickers' "Back" (QA-95) and now for `ConfirmModal`'s Cancel
 * (QA-117). Pass it as `className` on a `ghost` button.
 *
 * `!` on the background because `ghost` already sets `hover:bg-muted` — two utilities of equal
 * specificity, where the winner is decided by Tailwind's generated order rather than by anything at
 * the call site. Same idiom as the `link` variant's `!p-0`.
 *
 * 🚨 NOT the `ghost` default, yet. 14 buttons across the app are `ghost` and moving them all is a DS
 * ruling rather than a per-button tweak — the same note this string carried when it was
 * `FOOTER_BACK`. It is now asked for on the fourth of them, which is the argument for making it the
 * default; three inline copies still live in portal-www and should point here either way.
 */
export const GHOST_HAIRLINE_ON_HOVER =
    "border border-transparent hover:!bg-surface hover:border-border";

/**
 * `outline`, for a button sitting ON a --surface card. Hover fills --muted instead of --surface.
 *
 * `outline` hovers to --surface, which reads clearly against the --canvas page ground and
 * DISAPPEARS the moment the button sits on a white card — white filling in over white. QA-103
 * caught it on the Sessions tab's Reschedule, the button a therapist reaches for most, where it
 * was patched in place with `hover:!bg-canvas` and the DS ruling was flagged rather than taken:
 * "55 buttons across the app are `outline` and most of them sit on a surface-coloured card, so
 * the same hover is invisible for them too."
 *
 * --muted (#f5f1e8) rather than --canvas (#faf7f2), which the local patch used: on white, canvas
 * is a 2% step and muted is a 4% one, and this needs to register as a state change, not a
 * suspicion. It is the same fill `ghost` already hovers to, so the two quiet variants agree.
 *
 * `!` because `outline` already sets `hover:bg-surface` — two utilities of equal specificity,
 * decided by Tailwind's generated order rather than by anything at the call site. Same idiom as
 * GHOST_HAIRLINE_ON_HOVER above and the `link` variant's `!p-0`.
 *
 * 🚨 STILL OPT-IN, and that is the open question rather than a settled preference. If "most"
 * outline buttons really are on a card, the special case is the common one and this belongs in
 * the variant itself — at which point the buttons on --canvas are the ones that need a flag.
 */
export const OUTLINE_ON_SURFACE = "hover:!bg-muted";
export type ButtonSize = "sm" | "md" | "lg" | "xl";

interface ButtonOwnProps {
    variant?: ButtonVariant;
    size?: ButtonSize;
    leadingIcon?: ReactNode;
    trailingIcon?: ReactNode;
    full?: boolean;
}

/**
 * Pass `href` and this renders an `<a>` instead of a `<button>`, identical to
 * look at.
 *
 * WHY. Some actions that look like buttons ARE links, and the difference is not
 * cosmetic: a link gives cmd-click, middle-click, right-click → Save As, and
 * the "link" role a screen reader announces. E-sign's "Download signed consent"
 * hands over a legal document — losing Save As on it is a real loss — and its
 * tests assert `role="link"`. Before this, the three call sites that needed one
 * each hand-restated Button's own classes, which is precisely the drift the DS
 * exists to stop.
 *
 * `href` and `disabled` are mutually exclusive AT THE TYPE LEVEL, because an
 * `<a>` has no disabled state. `aria-disabled` on a link is a lie a keyboard
 * user can walk straight through. If an action can be unavailable, it is a
 * button — put the navigation in `onClick`.
 */
export type ButtonProps =
    | (ButtonOwnProps & ButtonHTMLAttributes<HTMLButtonElement> & { href?: never })
    | (ButtonOwnProps &
          AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; disabled?: never });

/* Disabled is a FLAT NEUTRAL per variant, not a faded brand fill (the rule Claude
   Design's Button already states). `disabled:opacity-60` used to fade the whole
   button — fill and label together — leaving a washed-out #77877c that still read
   as brand green, with the label at 3.70:1 against it, below AA.

   The FILLED variants keep a fill and swap the role colour for --disabled-fill,
   so a disabled primary stays a solid button rather than becoming a pale
   outline. White on it is 5.31:1. (It used to borrow --neutral, the status
   role, which is a cool green-grey and clashed with everything warm around it.) No border: the fill already draws the shape, and
   a light --border stroke around a mid-grey slab reads as a rendering artefact.
   The shadow does go, though — a lift is an affordance and there is nothing to
   press. This is a deliberately heavier disabled than a receding one; it was
   chosen on the look, against a lighter alternative that sat quieter on the page.

   Every disabled rule is paired with a `disabled:hover:` twin: :hover still
   matches a disabled button, so without it the rest-state hover fill wins back. */
const DISABLED_FILL = "disabled:bg-disabled-fill disabled:text-white disabled:border-0 disabled:shadow-none disabled:hover:bg-disabled-fill disabled:hover:text-white";
const DISABLED_OUTLINE = "disabled:bg-transparent disabled:text-tertiary disabled:border-border disabled:hover:bg-transparent disabled:hover:text-tertiary disabled:hover:border-border";
const DISABLED_BARE = "disabled:bg-transparent disabled:text-tertiary disabled:hover:bg-transparent disabled:hover:text-tertiary disabled:hover:no-underline";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
    // Coral fill, WHITE label. Operator ruling, 2026-08-31.
    //
    // 3.16:1, which is under AA's 4.5 for text. The alternatives measured:
    // --text-primary 5.98, --cta-ink 5.13, --surface 2.97. So white is the
    // best of the LIGHT options and the worst of the legible ones, and it was
    // chosen on the look over the number — the same call already recorded for
    // --success and --warning carrying white in Chip filled.
    //
    // No size rescues it: WCAG's large-text exemption (3:1) needs 18.66px bold
    // or 24px regular, and the biggest button here is xl at 18px/500.
    //
    // If it has to clear AA later the fix is the FILL, not the label: nothing
    // light passes on a coral this bright, so it would take a darker --cta.
    primary: `bg-cta text-white font-medium shadow-[var(--shadow-button)] hover:bg-cta-hover ${DISABLED_FILL}`,
    outline: `bg-transparent text-primary font-medium border border-border hover:bg-surface ${DISABLED_OUTLINE}`,
    // For a DARK ground. Every other variant assumes --canvas or --surface
    // behind it: on --cta, `outline` puts --text-primary at 1.16:1 and
    // disappears, which is exactly what happened when Couple Onboarding's
    // white-filled CTA was migrated to it. This is that button — a --surface
    // fill with --cta text, 12.39:1 — and it is the flow's PRIMARY action, so
    // it carries the shadow the filled variants do.
    inverse: `bg-surface text-cta-ink font-medium shadow-[var(--shadow-button)] hover:bg-muted ${DISABLED_FILL}`,
    // `inverse-bold` was "primary, but coral, for a dark ground". Since --cta
    // became the coral and primary took the dark ink, `primary` IS that button:
    // same fill, same label, same contrast. Kept as an alias so the call sites
    // that name it keep working and the intent stays readable at the point of
    // use — a dark-ground screen asking for the loud one.
    "inverse-bold": `bg-cta text-white font-medium shadow-[var(--shadow-button)] hover:bg-cta-hover ${DISABLED_FILL}`,
    ghost: `bg-transparent text-secondary font-medium hover:bg-muted hover:text-primary ${DISABLED_BARE}`,
    // --success, and it underlines rather than changing colour on hover: an in-text
    // action should read as a link, and colour alone is not a hover affordance.
    //
    // It was --interactive, which fails as TEXT: #6E9F94 on --surface measures 2.83:1,
    // where AA asks 4.5:1 of body text. Link buttons are text and nothing but text, so
    // this was the one variant with no fill or border to carry it. --success (#2C4C48)
    // is 8.9:1.
    //
    // The same split --cta already went through, for the same reason and in the same
    // direction: a token that is right as a FILL was being used as a TEXT COLOUR and
    // failing at it. --interactive keeps every one of its other jobs here — the focus
    // ring below is still ring-interactive, because a ring is not text.
    link: `bg-transparent text-success font-medium normal-case hover:underline !p-0 ${DISABLED_BARE}`,
    danger: `bg-danger text-white font-medium shadow-[var(--shadow-button)] hover:brightness-95 disabled:hover:brightness-100 ${DISABLED_FILL}`,
    "danger-outline":
        `bg-surface text-danger font-semibold border border-danger-bd hover:bg-danger-bg ${DISABLED_OUTLINE}`,
    // `border-transparent` at rest, not "no border": the outline appears on
    // hover without the button changing size, so the row beside it never
    // twitches under the cursor.
    "danger-ghost":
        `bg-transparent text-secondary font-medium border border-transparent hover:bg-surface hover:text-danger hover:border-danger-bd ${DISABLED_BARE} disabled:hover:border-transparent`,
};

/* ALL FOUR sizes are a HEIGHT, on the spacing scale: 24 / 32 / 40 / 48.

   They used to set vertical PADDING, which made the rendered height content +
   padding + border. Two consequences, both fixed by pinning the height:

   A filled and a stroked button at the same size were 2px apart, the outline
   taller by its stroke. Visible wherever the two share a row: the wizard footer
   measured Back at 42px beside Continue at 40px. With Preflight's border-box an
   explicit height absorbs the border, so every variant lands on the same number.

   And the ladder was not on the spacing scale, so a strict 8px grid collapsed
   it: lg (py-3 + 16px text) and xl both computed to 48px and the two sizes
   became one button. As heights the four steps ARE scale rungs and cannot
   drift into each other.

   lg is 40px, under the 44px touch minimum, which is why the flows use xl.

   It is also the only size that moves the LABEL as well as the box: 18px,
   where lg is 16px. Left at 16 it was lg's type in a taller box, which read as
   a stretched button rather than a bigger one. 18px is --type-body-lg's step,
   close to body copy, which is the risk to watch if this size ever escapes the
   flows onto a dense screen. `leading-none` holds throughout: with a fixed
   height, any line-height above 1 only fights the centring. */
const SIZE_CLASSES: Record<ButtonSize, string> = {
    sm: "h-6 px-4 text-xs",
    md: "h-8 px-6 text-sm",
    lg: "h-10 px-6 text-base",
    xl: "h-12 px-8 text-lg",
};

export const Button = forwardRef<HTMLButtonElement & HTMLAnchorElement, ButtonProps>(
    function Button(props, ref) {
    const {
        variant = "primary",
        size = "md",
        leadingIcon,
        trailingIcon,
        full = false,
        className = "",
        children,
        ...rest
    } = props;

    const classes = [
        "inline-flex items-center justify-center gap-2 rounded-[var(--radius-sm)]",
        // `capitalize` — CTAs are Title Case ("Get Started"), ruled 2026-08-31.
        // In CSS, not in the label strings, so callers keep writing sentence
        // case and the casing stays one decision. The `link` variant opts OUT
        // below: it is inline prose, not a CTA, and Title Case on a sentence
        // like "Go to the name step" reads as a mistake.
        "font-sans leading-none whitespace-nowrap tracking-[0.015em] cursor-pointer capitalize",
        // `border-color` is in the list for `danger-ghost`, whose outline
        // fades up from transparent on hover; without it the border snapped in
        // while the fill behind it eased.
        //
        // `scale` joins the list for the press below, and it has to be `scale`
        // rather than `transform`: Tailwind v4 compiles `scale-[0.98]` to the
        // standalone `scale` property, not to a transform function, so a
        // transition naming only `transform` animates nothing and the press
        // snaps in and out. Verified against the emitted rule, which is
        // `.active\:scale-\[0\.98\] { &:active { scale: 0.98 } }`.
        //
        // Same curve and duration as the colour change, so a press reads as one
        // event, and it is a compositor-only property, so nothing reflows.
        "transition-[background-color,border-color,color,box-shadow,scale] duration-[var(--dur-base)] ease-[var(--ease-out)]",
        // THE PRESS. Every button in the product had hover and focus states and
        // nothing at all for the moment of the click, so a tap on a touch
        // device — where there is no hover to feel — got no acknowledgment
        // until the next screen arrived.
        //
        // 0.98, and scale rather than a downward translate: at the 48px xl size
        // a 1px nudge is invisible, and on a full-width CTA a translate slides
        // the label against a stationary edge. `disabled:` is excluded, since a
        // control that cannot act must not answer as though it did.
        "active:scale-[0.98] disabled:active:scale-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-interactive focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        full ? "w-full" : "",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const inner = (
        <>
            {leadingIcon}
            {children}
            {trailingIcon}
        </>
    );

    if (typeof (rest as { href?: string }).href === "string") {
        return (
            <a ref={ref} className={classes} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}>
                {inner}
            </a>
        );
    }

    // `type="button"` by default — an unqualified <button> inside a <form>
    // submits it, which is never what an unspecified DS button meant.
    const { type = "button", ...buttonRest } = rest as ButtonHTMLAttributes<HTMLButtonElement>;
    return (
        <button ref={ref} type={type} className={classes} {...buttonRest}>
            {inner}
        </button>
    );
});
